import { useCallback, useEffect, useState } from 'react';
import { chatbotService } from '../../../services/chatbotService';
import { encodeWav, framesDurationMs } from '../utils/wavEncoder';
import {
    enqueueVoiceOutbox,
    deleteVoiceOutbox,
    recordAttemptFailure,
    type VoiceOutboxRecord,
} from '../utils/voiceOutbox';
import type { Message, SendVoiceResponse, VoiceQuotaStatus } from '../../../types/messaging';
import { useMessagingStore } from '../../../stores/messagingStore';
import { useTokens } from '../../../contexts/TokenContext';

/**
 * useVoiceChatSession — orchestrates upload of recorded PCM frames and
 * tracks the quota / response state for the PTT UI.
 *
 * Network-failure safety: every recording is mirrored into IndexedDB before
 * the upload is attempted. If the upload throws (network drop, timeout,
 * server 5xx), the encoded WAV stays in the outbox and the background
 * scheduler (useVoiceOutboxScheduler in VoiceChatInput) retries on
 * exponential backoff. The bubble's metadata reflects the queue state
 * (queued / retrying / failed) so the user sees their recording is safe
 * instead of mysteriously vanishing.
 *
 * Bot reply itself comes through the existing messaging WebSocket
 * ('message' event), so this hook is small: it owns the request lifecycle
 * for `POST /messaging/ai/voice` + the local quota cache.
 */

export type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export interface UseVoiceChatSessionArgs {
    conversationId: string;
    /** Current user's id — used to author the optimistic user bubble. */
    currentUserId: string;
}

export interface VoiceChatSessionHandle {
    uploadState: UploadState;
    lastResponse: SendVoiceResponse | null;
    error: string | null;
    quota: VoiceQuotaStatus | null;
    sendFrames: (frames: Float32Array[], sampleRate: number) => Promise<SendVoiceResponse | null>;
    refreshQuota: () => Promise<void>;
}

/**
 * Internal: post a single outbox record to the backend, then update the
 * local message store and the IDB outbox based on the result. Shared by
 * the inline send path AND the background scheduler so both surfaces
 * behave identically.
 */
export async function uploadVoiceOutboxRecord(
    record: VoiceOutboxRecord,
    onTokenChange: () => void,
): Promise<SendVoiceResponse> {
    const res = await chatbotService.sendVoice({
        conversationId: record.conversationId,
        audio: record.wavBlob,
    });

    if (res.ok && res.messageId) {
        useMessagingStore.getState().updateLocalMessage(record.tempId, {
            id: res.messageId,
            content: res.transcript ?? '',
            metadata: {
                isVoiceMode: true,
                isTranscribing: false,
                classification: res.classification,
                detectedLanguage: res.detectedLanguage,
                countedTowardQuota: res.countedTowardQuota ?? false,
            },
        });
        await deleteVoiceOutbox(record.tempId);
        if (res.charge === 'coin') onTokenChange();
    } else {
        // Server refused (inaudible / quota / etc.) — drop the bubble +
        // the outbox entry; the toast layer surfaces the reason.
        useMessagingStore.getState().removeLocalMessage(record.tempId);
        await deleteVoiceOutbox(record.tempId);
    }

    return res;
}

export function useVoiceChatSession(args: UseVoiceChatSessionArgs): VoiceChatSessionHandle {
    const { conversationId, currentUserId } = args;
    const [uploadState, setUploadState] = useState<UploadState>('idle');
    const [lastResponse, setLastResponse] = useState<SendVoiceResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [quota, setQuota] = useState<VoiceQuotaStatus | null>(null);
    const { refreshStats: refreshTokenStats } = useTokens();

    const refreshQuota = useCallback(async () => {
        try {
            const q = await chatbotService.getVoiceQuota();
            setQuota(q);
        } catch {
            // Quota fetch failures are non-fatal — UI just hides the chip.
        }
    }, []);

    const sendFrames = useCallback(
        async (frames: Float32Array[], sampleRate: number): Promise<SendVoiceResponse | null> => {
            setUploadState('uploading');
            setError(null);

            const tempId = `voice-temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const recordedMs = framesDurationMs(frames, sampleRate);
            const wavBlob = encodeWav(frames, sampleRate);

            const optimisticMessage: Message = {
                id: tempId,
                conversationId,
                senderId: currentUserId,
                content: '',
                isRead: true,
                createdAt: new Date().toISOString(),
                audioUrl: null,
                audioDurationMs: recordedMs,
                metadata: {
                    isVoiceMode: true,
                    isTranscribing: true,
                    queueState: 'sending',
                },
            };
            useMessagingStore.getState().addLocalMessage(optimisticMessage);

            // Persist FIRST, then attempt upload. If the user closes the tab
            // mid-upload the recording is recoverable on next mount.
            try {
                await enqueueVoiceOutbox({
                    tempId,
                    conversationId,
                    wavBlob,
                    sampleRate,
                    createdAt: Date.now(),
                });
            } catch (err) {
                // IDB unavailable (private mode quirks, full disk) — fall
                // back to ephemeral upload. The user just loses retry
                // safety, which is the previous behaviour.
                useMessagingStore.getState().updateLocalMessage(tempId, {
                    metadata: { isVoiceMode: true, isTranscribing: true, queueState: 'sending' },
                });
                console.warn('[voice-outbox] enqueue failed, falling back to ephemeral upload', err);
            }

            try {
                const record: VoiceOutboxRecord = {
                    tempId,
                    conversationId,
                    wavBlob,
                    sampleRate,
                    createdAt: Date.now(),
                    attempts: 0,
                    nextAttemptAt: Date.now(),
                    lastError: null,
                    dead: false,
                };
                const res = await uploadVoiceOutboxRecord(record, refreshTokenStats);
                setLastResponse(res);
                setUploadState('success');
                refreshQuota();
                return res;
            } catch (err) {
                const errMsg = (err as Error).message;
                // Mark for retry — the scheduler will pick it up on the
                // next tick. Bubble switches to "queued/retrying" state
                // so the user knows it's not lost.
                const updated = await recordAttemptFailure(tempId, errMsg).catch(() => null);
                useMessagingStore.getState().updateLocalMessage(tempId, {
                    metadata: {
                        isVoiceMode: true,
                        isTranscribing: false,
                        queueState: updated?.dead ? 'failed' : 'retrying',
                        attempts: updated?.attempts ?? 1,
                        lastError: errMsg,
                    },
                });
                setError(errMsg);
                setUploadState('error');
                return null;
            }
        },
        [conversationId, currentUserId, refreshQuota, refreshTokenStats],
    );

    useEffect(() => {
        // Quota refresh on mount; component-level prewarm lives in VoiceChatInput.
    }, []);

    return { uploadState, lastResponse, error, quota, sendFrames, refreshQuota };
}
